import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, IconButton, Modal, Typography } from "@mui/material";

import { CloseButtonIcon, CompareModalIcon } from "../../assets/icons/components-icons";

import { JobCandidate } from "../../api/types/job.types";
import { useCompareCandidates } from "../../api/mutations/useCompareCandidates";

import CellValue from "./CellValue";
import UserDataCell from "./UserDataCell";
import CompareLoading from "./CompareLoading";

export const CompareCandidatesModal = ({
  open,
  onClose,
  selectedCandidates,
  jobId,
}: {
  open: boolean;
  onClose: () => void;
  selectedCandidates: JobCandidate[];
  jobId: string;
}) => {
  const { t } = useTranslation("goHire", { keyPrefix: "components.compareCandidatesModal" });
  const { t: tUser } = useTranslation("common", { keyPrefix: "user" });

  const { compareCandidatesMutate, data: comparisonData, isPending, isError } = useCompareCandidates();

  useEffect(() => {
    if (open && selectedCandidates.length > 0) {
      compareCandidatesMutate({
        jobId: Number(jobId),
        candidateIds: selectedCandidates.map((c) => c.id),
      });
    }
  }, [open, selectedCandidates, jobId, compareCandidatesMutate]);

  const candidates = comparisonData?.data?.candidates || [];

  const handleClose = () => onClose();

  useEffect(() => {
    if (isError) {
      onClose();
      toast.error(t("errorLoadingData"));
    }
  }, [isError]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Typography sx={styles.headerTitle}>
            <CompareModalIcon />
            {t("compare")}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseButtonIcon />
          </IconButton>
        </Box>

        {isPending && <CompareLoading numCandidates={selectedCandidates.length} />}

        {!isPending && !isError && (
          <Box sx={styles.gridContainer(candidates.length)}>
            {/* Header row */}
            <Box sx={styles.rowLabelCell} />
            {candidates.map((c) => (
              <UserDataCell key={c.candidateId} jobId={jobId} candidate={c} />
            ))}

            <Box sx={styles.rowSectionTitleCell}>{t("overview")}</Box>

            {/* Experience */}
            <Box sx={styles.rowLabelCell}>{t("experience")}</Box>
            {candidates.map((c) => (
              <CellValue key={`exp-${c.candidateId}`} value={c.overview.experienceYears || "-"} />
            ))}

            {/* Key skills match */}
            <Box sx={styles.rowLabelCell}>{t("keySkillsMatch")}</Box>
            {candidates.map((c) => (
              <CellValue key={`ksm-${c.candidateId}`} value={c.overview.keySkillsMatch?.skills || []} />
            ))}

            {personaFields.some((field) => candidates.some((c) => (c.personaOverview as any)[field.key])) && (
              <Divider sx={{ gridColumn: "1 / -1" }} />
            )}
            {personaFields.map((field) => {
              const hasData = candidates.some((c) => {
                const val = (c.personaOverview as any)[field.key];
                return val !== undefined && val !== null && val !== "";
              });
              if (!hasData) return null;

              return (
                <>
                  <Box sx={styles.rowLabelCell}>{t(field.label)}</Box>
                  {candidates.map((c) => {
                    const rawValue = (c.personaOverview as any)[field.key];
                    let formattedValue: any = rawValue;

                    if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
                      if (field.type === "height") {
                        formattedValue = t("heightCM", { height: rawValue });
                      } else if (field.type === "weight") {
                        formattedValue = t("weightKG", { weight: rawValue });
                      } else if (field.type === "footType") {
                        formattedValue = tUser("footType." + rawValue);
                      } else if (field.type === "playerPosition") {
                        formattedValue = tUser("playerPosition." + rawValue);
                      } else if (field.key === "certificationAndLicenses") {
                        // Extract titles from certification objects
                        formattedValue = Array.isArray(rawValue) ? rawValue.map((cert: any) => cert.title) : [];
                      }
                    } else {
                      formattedValue = "-";
                    }

                    return <CellValue key={`${field.key}-${c.candidateId}`} value={formattedValue} />;
                  })}
                </>
              );
            })}

            <Divider sx={{ gridColumn: "1 / -1" }} />
            <Box sx={styles.rowSectionTitleCell}>{t("nationalityAndLocation")}</Box>

            {/* Nationality */}
            <Box sx={styles.rowLabelCell}>{t("nationality")}</Box>
            {candidates.map((c) => (
              <CellValue key={`nat-${c.candidateId}`} value={c.nationalityAndLocation.nationality?.name || "-"} />
            ))}

            {/* Residence country */}
            <Box sx={styles.rowLabelCell}>{t("residenceCountry")}</Box>
            {candidates.map((c) => (
              <CellValue key={`res-${c.candidateId}`} value={c.nationalityAndLocation.residenceCountry?.name || "-"} />
            ))}

            <Divider sx={{ gridColumn: "1 / -1" }} />
            <Box sx={styles.rowSectionTitleCell}>{t("submittedForm")}</Box>

            {/* Languages spoken */}
            <Box sx={styles.rowLabelCell}>{t("languagesSpoken")}</Box>
            {candidates.map((c) => (
              <CellValue key={`lang-${c.candidateId}`} value={c.submittedForm.languagesSpoken || []} />
            ))}

            {/* Willing to relocate */}
            <Box sx={styles.rowLabelCell}>{t("willingToRelocate")}</Box>
            {candidates.map((c) => (
              <CellValue key={`rel-${c.candidateId}`} value={c.submittedForm.willingToRelocate} />
            ))}

            {/* Submitted video */}
            <Box sx={styles.rowLabelCell}>{t("submittedVideo")}</Box>
            {candidates.map((c) => (
              <CellValue key={`vid-${c.candidateId}`} value={c.submittedForm.submittedVideo} />
            ))}

            {/* Any major injury */}
            <Box sx={styles.rowLabelCell}>{t("anyMajorInjury")}</Box>
            {candidates.map((c) => (
              <CellValue key={`inj-${c.candidateId}`} value={c.submittedForm.anyMajorInjury} />
            ))}

            {/* Clubs/Academies worked in */}
            <Box sx={styles.rowLabelCell}>{t("clubsAcademiesWorkedIn")}</Box>
            {candidates.map((c) => (
              <CellValue
                key={`clubs-${c.candidateId}`}
                value={c.submittedForm.clubsAcademiesWorkedIn?.map((club) => club.name) || []}
              />
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

const personaFields = [
  { key: "age", label: "age" },
  { key: "heightCm", label: "height", type: "height" },
  { key: "weightKg", label: "weight", type: "weight" },
  { key: "strongFoot", label: "strongFoot", type: "footType" },
  { key: "primaryPosition", label: "primaryPosition", type: "playerPosition" },
  { key: "otherPosition", label: "otherPosition", type: "playerPosition" },
  { key: "totalCareerTransfers", label: "totalCareerTransfers" },
  { key: "totalRepresented", label: "totalRepresented" },
  { key: "preferredFormation", label: "preferredFormation" },
  { key: "totalTeamsCoached", label: "teamsCoached" },
  { key: "countriesCoachedIn", label: "countriesCoachedIn" },
  { key: "certificationAndLicenses", label: "certificationAndLicenses" },
  { key: "representedClubs", label: "representedClubs" },
  { key: "totalScoutedPlayers", label: "totalScoutedPlayers" },
  { key: "locations", label: "locations" },
];

const styles = {
  container: {
    width: { xs: "95%", md: "fit-content" },
    height: "fit-content",
    maxHeight: "100vh",
    position: "absolute",
    top: "0%",
    right: "0%",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "32px 16px 32px 32px",
    boxShadow:
      "0 20px 24px -4px var(--Colors-Effects-Shadows-shadow-xl_01, rgba(16, 24, 40, 0.08)), 0 8px 8px -4px var(--Colors-Effects-Shadows-shadow-xl_02, rgba(16, 24, 40, 0.03))",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitle: {
    fontFamily: "Montserrat",
    color: "var(--colors-text-text-primary-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
  gridContainer: (numCandidates: number) => ({
    display: "grid",
    gridTemplateColumns: `256px repeat(${numCandidates}, 164px)`,
    gap: "32px",
    maxHeight: "100%",
    overflowY: "auto",
    pr: "16px",
  }),
  rowSectionTitleCell: {
    fontFamily: "Montserrat",
    color: "var(--Brand-700, #076D0D)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
    gridColumn: "1 / -1",
  },
  rowLabelCell: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  statusContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    minHeight: "300px",
    minWidth: "600px",
  },
};

export const useCompareCandidatesModal = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return {
    openCompareCandidatesModal: open,
    handleOpenCompareCandidatesModal: handleOpen,
    handleCloseCompareCandidatesModal: handleClose,
  };
};
